import { Popup } from '@approvalmax/ui';
import { ConditionWrapper, Grid } from '@approvalmax/ui/src/components';
import { constants } from 'modules/common';
import { backend, domain } from 'modules/data';
import { RequestStatusV2 } from 'modules/data/domain';
import { useSelector } from 'modules/react-redux';
import { uploadAttachments } from 'pages/requestForm/actions';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useToggle } from 'react-use';
import { ManualJournalImportPopup } from 'shared/components';
import { ImportManualJournal } from 'shared/data/webApp/v1';
import { useUserBetaFeatures } from 'shared/hooks';

import { AttachmentsSection, NoteForApproversSection, RequesterInstructionSection } from '../../../components';
import FileDropProvider from '../../../components/FileDropProvider';
import { getContext } from '../../../selectors/pageSelectors';
import {
    getReadonlyFields,
    getRequestEditMode,
    getRequiredFields,
    RequestEditMode,
} from '../../../selectors/requestSelectors';
import { getXeroContext } from '../../../selectors/xero';
import ActionsBlock from '../../form/ActionsBlock/ActionsBlock';
import FilesSection from '../../form/FilesSection';
import {
    ManualJournalFooter,
    ManualJournalHeaderFields,
    ManualJournalImportPopup as ManualJournalImportPopupOld,
    ManualJournalLines,
    ManualJournalSummary,
} from './components';
import {
    calculateAmounts,
    checkManualJournalLineIsEmpty,
    checkManualJournalLinesAreEmpty,
    getValidationErrors,
    mapManualJournalLines,
} from './XeroManualJournalRequestForm.helpers';
import { useFields, useManualJournalLines } from './XeroManualJournalRequestForm.hooks';
import { messages } from './XeroManualJournalRequestForm.messages';
import {
    Content,
    ContentInner,
    Root,
    Toolbar,
    ToolbarHeader,
    ToolbarHeaderText,
} from './XeroManualJournalRequestForm.styles';
import { XeroManualJournalRequestFormProps } from './XeroManualJournalRequestForm.types';

const { xeroConstants } = constants;

const XeroManualJournalRequestForm = memo<XeroManualJournalRequestFormProps>((props) => {
    const { request } = props;

    const dispatch = useDispatch();

    const pageContext = useSelector(getContext);
    const xeroContext = useSelector(getXeroContext);
    const editMode = useSelector((state) => getRequestEditMode(state, request));

    const { trackingCategories, emptyTaxCode, taxCodes } = xeroContext;

    const { fieldValues, onFieldChange, setFieldsValuesBulk } = useFields();

    const requiredFields = useSelector((state) => getRequiredFields(state, request));
    const readonlyFields = useSelector((state) => getReadonlyFields(state, request));

    const [showErrors, setShowErrors] = useState(false);
    const [isImportPopupOpen, toggleIsImportPopupOpen] = useToggle(false);

    const {
        manualJournalLines,
        setManualJournalLines,
        onManualJournalLineAdd,
        onManualJournalLineClone,
        onManualJournalLineFieldUpdate,
        onManualJournalLineRemove,
        onManualJournalLinesReorder,
        onManualJournalLineTrackingFieldUpdate,
    } = useManualJournalLines(
        fieldValues.lineAmountType,
        trackingCategories,
        fieldValues.defaultNarrationToDescription ? fieldValues.narration : null
    );

    useEffect(() => {
        const details = request.details;

        setFieldsValuesBulk({
            journalDate: details.date,
            narration: details.narration || '',
            showOnCashBasisReports: details.showOnCashBasisReports,
            lineAmountType: details.lineAmountType || domain.LineAmountType.NoTax,
        });

        if (details.lineItems.length > 0) {
            setManualJournalLines(details.lineItems);
        }
    }, [request.details, setFieldsValuesBulk, setManualJournalLines]);

    const onUploadAttachments = useCallback(
        (files: File[]) => {
            dispatch(uploadAttachments({ files }));
        },
        [dispatch]
    );

    const onLineAmountTypeChange = useCallback(
        (value: { id: domain.LineAmountType }) => {
            onFieldChange('lineAmountType', value.id);
        },
        [onFieldChange]
    );

    const showErrorsOnEmptyLine = useMemo(() => {
        return checkManualJournalLinesAreEmpty(manualJournalLines);
    }, [manualJournalLines]);

    const getRequestTransfer = (): backend.Transfer<backend.transfers.RequestEditTransfer> => {
        const amounts = calculateAmounts(manualJournalLines, fieldValues.lineAmountType);

        const transfer: backend.Transfer<backend.transfers.RequestEditTransfer> = {
            requestId: request.id,
            amount: amounts.debitTotal,
            currency: request.currency ?? null,
            attachments: request.attachments.map((attachment) => attachment.id),
            requestNote: request.requestNote.value,
            templateVersion: pageContext.templateVersion,
            companyId: request.companyId,
        };

        transfer.xeroManualJournalDetails = {
            date: fieldValues.journalDate,
            narration: fieldValues.narration,
            showOnCashBasisReports: fieldValues.showOnCashBasisReports,
            lineAmountType: fieldValues.lineAmountType,
            debitSubTotal: amounts.debitSubTotal,
            creditSubTotal: amounts.creditSubTotal,
            debitTotalTax: amounts.debitTotalTax,
            creditTotalTax: amounts.creditTotalTax,
            lineItems: manualJournalLines
                .filter((line) => !checkManualJournalLineIsEmpty(line))
                .map((line) => {
                    return {
                        id: line.id.startsWith('xeroManualJournalLine') ? null : line.id,
                        accountId: line.account?.id ?? '',
                        description: line.description ?? '',
                        amount: line.amount ?? 0,
                        amountSide: line.amountType || null,
                        taxCode:
                            fieldValues.lineAmountType === domain.LineAmountType.NoTax ? emptyTaxCode.id : line.tax?.id,
                        taxAmount: (line.taxAmount ?? 0).toString(),
                        trackingCategories: line.tracking.map((trackingCategory) => ({
                            id: trackingCategory.category.id,
                            valueId: trackingCategory.value.id,
                        })),
                    };
                }),
        };

        return transfer;
    };

    const getRequestValidationErrors = useCallback(async () => {
        setShowErrors(true);

        return getValidationErrors(fieldValues, manualJournalLines, requiredFields.fieldIds, trackingCategories);
    }, [fieldValues, manualJournalLines, requiredFields, trackingCategories]);

    const onImportManualJournal = useCallback(
        (data: ImportManualJournal['response']) => {
            toggleIsImportPopupOpen(false);

            setFieldsValuesBulk({
                journalDate: data.date,
                narration: data.narration,
            });

            if (data.lineItems.length > 0) {
                setManualJournalLines(
                    mapManualJournalLines(data.lineItems, fieldValues.lineAmountType, trackingCategories, taxCodes)
                );
            }
        },
        [
            fieldValues.lineAmountType,
            setFieldsValuesBulk,
            setManualJournalLines,
            taxCodes,
            toggleIsImportPopupOpen,
            trackingCategories,
        ]
    );

    const { isBelgradeUIFilesUploadSection, isBelgradeUIManualJournalImportPopup, isBelgradeUILineItemsTotalSection } =
        useUserBetaFeatures();
    const isImportFileButtonHidden =
        request.statusV2 === RequestStatusV2.OnReview && editMode === RequestEditMode.Reviewer;

    return (
        <ConditionWrapper
            condition={!isBelgradeUIFilesUploadSection}
            wrapperIf={(children) => (
                <FileDropProvider
                    maxSize={pageContext.attachmentMaxSize}
                    allowedFileExtensions={xeroConstants.XERO_ALLOWED_FILE_EXTENSIONS}
                    onUpload={onUploadAttachments}
                    disabled={isImportPopupOpen}
                >
                    {children}
                </FileDropProvider>
            )}
        >
            <Root>
                <Toolbar>
                    <ToolbarHeader>
                        <ToolbarHeaderText>{messages.headerText}</ToolbarHeaderText>

                        <ActionsBlock
                            isImportFileButtonHidden={isImportFileButtonHidden}
                            customGetRequestTransfer={getRequestTransfer}
                            customRequestValidationFn={getRequestValidationErrors}
                            onOpenImportFilePopup={toggleIsImportPopupOpen}
                        />
                    </ToolbarHeader>
                </Toolbar>

                <Content>
                    <ContentInner>
                        <RequesterInstructionSection instructionText={pageContext.requesterInstruction} />

                        <ManualJournalHeaderFields
                            fieldValues={fieldValues}
                            showErrors={showErrors}
                            onFieldChange={onFieldChange}
                        />

                        <ConditionWrapper
                            condition={isBelgradeUIFilesUploadSection}
                            wrapperIf={(children) => (
                                <Grid gridTemplateColumns='1fr 1fr' gap={16}>
                                    {children}

                                    <AttachmentsSection
                                        request={request}
                                        editMode={editMode}
                                        maxFilesInList={9}
                                        extensions={xeroConstants.xeroAllowedFileExtensions}
                                        readOnly={isImportPopupOpen}
                                    />
                                </Grid>
                            )}
                        >
                            <NoteForApproversSection />
                        </ConditionWrapper>

                        <ManualJournalLines
                            lines={manualJournalLines}
                            lineAmountType={fieldValues.lineAmountType}
                            showErrors={showErrors}
                            showErrorsOnEmptyLine={showErrorsOnEmptyLine}
                            readonlyFields={readonlyFields.fieldIds}
                            requiredFields={requiredFields.fieldIds}
                            onAddLine={onManualJournalLineAdd}
                            onCloneLine={onManualJournalLineClone}
                            onRemoveLine={onManualJournalLineRemove}
                            onReorderLines={onManualJournalLinesReorder}
                            onLineFieldUpdate={onManualJournalLineFieldUpdate}
                            onLineAmountTypeChange={onLineAmountTypeChange}
                            onLineTrackingCategoryChange={onManualJournalLineTrackingFieldUpdate}
                        />

                        {isBelgradeUILineItemsTotalSection ? (
                            <ManualJournalSummary
                                lines={manualJournalLines}
                                currency={request.currency}
                                lineAmountType={fieldValues.lineAmountType}
                            />
                        ) : (
                            <ManualJournalFooter
                                lines={manualJournalLines}
                                currency={request.currency}
                                lineAmountType={fieldValues.lineAmountType}
                            />
                        )}

                        {!isBelgradeUIFilesUploadSection && <FilesSection />}

                        {isBelgradeUIManualJournalImportPopup ? (
                            <ManualJournalImportPopup
                                open={isImportPopupOpen}
                                onToggle={toggleIsImportPopupOpen}
                                onSuccess={onImportManualJournal}
                            />
                        ) : (
                            <Popup isOpen={isImportPopupOpen} onRequestClose={toggleIsImportPopupOpen} disableAutoClose>
                                <ManualJournalImportPopupOld onSuccess={onImportManualJournal} />
                            </Popup>
                        )}
                    </ContentInner>
                </Content>
            </Root>
        </ConditionWrapper>
    );
});

export default XeroManualJournalRequestForm;
